import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Edit, X, Check, Loader2 } from 'lucide-react';

const AiChatbotEditableField = ({
  value,
  onChange,
  onSave,
  placeholder,
  rows,
  type = "text",
  label,
  helpText,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const [justSaved, setJustSaved] = useState(false);

  const handleEdit = () => {
    setTempValue(value);
    setIsEditing(true);
    setJustSaved(false);
  };

  const handleCancel = () => {
    setTempValue(value);
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(tempValue);
      setIsEditing(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 3000);
    } catch (error) {
      console.error("Save failed:", error);
      setTempValue(value);
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="font-medium text-gray-900">{label}</label>
          <div className="flex items-center space-x-2">
            <motion.button
              onClick={handleCancel}
              className="p-1 text-gray-600 hover:text-[#01CC54] hover:bg-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-[#01CC54]/40"
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.95 }}
            >
              <X size={16} />
            </motion.button>
            <motion.button
              onClick={handleSave}
              disabled={isSaving}
              className={`px-3 py-1 rounded text-sm font-medium shadow-lg focus:outline-none focus:ring-2 focus:ring-emerald-400/40 ${
                isSaving
                  ? "bg-[#048AAD]/60 text-white cursor-not-allowed"
                  : "bg-[#048AAD] text-white hover:bg-[#048AAD]/90"
              }`}
              whileHover={!isSaving ? { scale: 1.02 } : {}}
              whileTap={!isSaving ? { scale: 0.98 } : {}}
            >
              {isSaving ? (
                <>
                  <Loader2 size={14} className="inline mr-1 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check size={14} className="inline mr-1" />
                  Save
                </>
              )}
            </motion.button>
          </div>
        </div>

        {rows ? (
          <textarea
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            rows={rows}
            className="w-full rounded-lg p-3 text-sm text-gray-900 placeholder-gray-400 border border-gray-200 bg-white focus:outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition"
            placeholder={placeholder}
            autoFocus
          />
        ) : (
          <input
            type={type}
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            className="w-full rounded-lg p-3 text-sm text-gray-900 placeholder-gray-400 border border-gray-200 bg-white focus:outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition"
            placeholder={placeholder}
            autoFocus
          />
        )}

        {helpText && <p className="text-xs text-gray-500">{helpText}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="font-medium text-gray-900">{label}</label>
        <motion.button
          onClick={handleEdit}
          className="p-1 text-gray-600 hover:text-[#01CC54] hover:bg-gray-100 rounded transition focus:outline-none focus:ring-2 focus:ring-[#01CC54]/40"
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.95 }}
        >
          <Edit size={16} />
        </motion.button>
      </div>

      <AnimatePresence>
        <motion.div
          className={`p-3 rounded-lg border transition-all duration-200 bg-gray-50 border-gray-200`}
          initial={justSaved ? { scale: 1.02 } : {}}
          animate={justSaved ? { scale: 1 } : {}}
          transition={{ duration: 0.2 }}
        >
          {justSaved && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center text-[#01CC54] text-sm mb-2"
            >
              <Check size={14} className="mr-1" />
              Saved successfully!
            </motion.div>
          )}

          <div className={`text-sm ${value ? "text-gray-900" : "text-gray-500"}`}>
            {rows ? (
              <div className="whitespace-pre-wrap">{value || placeholder}</div>
            ) : (
              <span>{value || placeholder}</span>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      {helpText && <p className="text-xs text-gray-400">{helpText}</p>}
    </div>
  );
};

export default AiChatbotEditableField;

